import { Getter } from '../../Getter';
import { HttpError } from '../../HttpError';
import { Response } from './Models/ApiResponse';
import {
  CommentContext,
  CommentType,
  DateRange,
} from '../../../../models/CommentHistory';
import { Severity } from 'Common/components/Molecules/CommentSeverityIcon';
import { toCommentContext } from './Converter/ApiCommentContextConverter';
import isValidUUID from 'uuid-validate';
import URI from 'urijs';

type ApiResponse = Response | null | undefined;

interface CommentContextOutput {
  commentContexts: CommentContext[];
  totalCommentCount: number;
}

interface DataResponse {
  data?: CommentContextOutput;
  error?: never;
}

interface ErrorResponse {
  data?: never;
  error: HttpError;
}
export type ClientOutput = DataResponse | ErrorResponse;

export interface ClientInput {
  organizationUUID: string;
  dateRange: DateRange;
  commentType: CommentType;
  severity?: Severity;
  category?: string;
  author?: string;
  repositoryUUID?: string;
  page: number;
  sortBy: 'severity' | 'created_at';
}

export class CommentContextClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(input: ClientInput): Promise<ClientOutput> {
    const error = this.validate(input);
    if (error) {
      return { error };
    }

    const response = await this.tryRetrieveAsync(input);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    return {
      data: {
        commentContexts: response.data?.map(toCommentContext) ?? [],
        totalCommentCount: response.total,
      },
    };
  }

  public validate({
    organizationUUID,
    commentType,
    severity,
    category,
    repositoryUUID,
  }: ClientInput): HttpError | undefined {
    if (
      !isValidUUID(organizationUUID) ||
      (repositoryUUID && !isValidUUID(repositoryUUID))
    ) {
      // equivalent to the response we'd get on the server
      return new HttpError(400);
    }

    if (
      commentType !== 'inline' &&
      (severity !== undefined || category !== undefined)
    ) {
      // equivalent to the response we'd get on the server
      return new HttpError(400);
    }

    return;
  }

  private async tryRetrieveAsync(
    input: ClientInput
  ): Promise<ApiResponse | HttpError> {
    const href = this.getHref(input);

    return await this.getter.get(href);
  }

  public getHref({
    organizationUUID,
    dateRange,
    commentType,
    severity,
    category,
    author,
    repositoryUUID,
    sortBy,
    page,
  }: ClientInput): string {
    const href = URI('/api/v1/organizations/')
      .segmentCoded(organizationUUID)
      .segment('comment-stream')
      .addQuery({ date_range: dateRange })
      .addQuery({ comment_type: commentType })
      .addQuery({ page });

    if (severity) {
      href.addQuery({ severity });
    }

    if (category) {
      href.addQuery({ category });
    }

    if (author) {
      href.addQuery({ author_user_name: author });
    }

    if (repositoryUUID) {
      href.addQuery({ repository_uuid: repositoryUUID });
    }

    href.addQuery({ sort_by: sortBy });

    return href.toString();
  }
}
